import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export interface NotifyStudentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    subject: string;
    message: string;
    inApp: boolean;
    email: boolean;
    sms: boolean;
  }) => Promise<void>;
  selectedCount: number;
  smsAvailable?: boolean;
  /** Shown when messaging a single student from detail page */
  deliveryHint?: string;
}

export function NotifyStudentsModal({
  isOpen,
  onClose,
  onSubmit,
  selectedCount,
  smsAvailable = false,
  deliveryHint,
}: NotifyStudentsModalProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [inApp, setInApp] = useState(true);
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    if (!inApp && !email && !sms) return;
    setSending(true);
    try {
      await onSubmit({
        subject: subject.trim(),
        message: message.trim(),
        inApp,
        email,
        sms: smsAvailable ? sms : false,
      });
      setSubject('');
      setMessage('');
      setSms(false);
      onClose();
    } finally {
      setSending(false);
    }
  };

  const smsChars = message.length + subject.length + 40;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-950">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Message students</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          {selectedCount} student{selectedCount !== 1 ? 's' : ''} selected. Students with their own
          account are messaged directly; messages for children go to their parent or guardian (one
          message per parent or guardian when they have multiple children). Roster-only students can
          receive email when an invite address is on file.
        </p>
        {deliveryHint ? (
          <p className="mb-4 text-sm text-muted-foreground">{deliveryHint}</p>
        ) : null}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              required
              maxLength={200}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              required
              maxLength={5000}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={inApp}
                onChange={(e) => setInApp(e.target.checked)}
                className="rounded border-gray-300"
              />
              In-app notification
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={email}
                onChange={(e) => setEmail(e.target.checked)}
                className="rounded border-gray-300"
              />
              Email
            </label>
            {smsAvailable && (
              <label className="flex flex-col gap-1 text-sm text-gray-800 dark:text-gray-200">
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sms}
                    onChange={(e) => setSms(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  SMS (opted-in students and parents or guardians only)
                </span>
                {sms && (
                  <span className="text-xs text-muted-foreground pl-6">
                    Keep brief — about {smsChars} characters before truncation (~160 per segment).
                  </span>
                )}
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending || (!inApp && !email && !(smsAvailable && sms))}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** @deprecated Use NotifyStudentsModal */
export const BulkNotifyStudentsModal = NotifyStudentsModal;
